import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, startOfMonth, endOfMonth, getDaysInMonth, addMonths, subMonths } from 'date-fns';
import {
  CalendarDays, Users, CheckCircle2, XCircle, Clock,
  ChevronLeft, ChevronRight, Loader2, Plus, Check, X,
  AlertCircle, Coffee, Home,
} from 'lucide-react';
import { api } from '@/lib/api';
import { clsx } from 'clsx';

const TABS = ['attendance', 'roster', 'leaves'] as const;
type Tab = typeof TABS[number];

const STATUS_CONFIG = {
  present:   { label: 'P',   color: 'bg-green-100 text-green-700',  icon: CheckCircle2 },
  absent:    { label: 'A',   color: 'bg-red-100 text-red-700',      icon: XCircle },
  half_day:  { label: 'H',   color: 'bg-yellow-100 text-yellow-700',icon: Clock },
  late:      { label: 'L',   color: 'bg-orange-100 text-orange-700',icon: Clock },
  on_leave:  { label: 'OL',  color: 'bg-blue-100 text-blue-700',    icon: Home },
  holiday:   { label: 'HOL', color: 'bg-purple-100 text-purple-700',icon: Coffee },
  comp_off:  { label: 'CO',  color: 'bg-teal-100 text-teal-700',    icon: Coffee },
} as const;

const LEAVE_STATUS = {
  pending:  { label: 'Pending',  color: 'bg-yellow-100 text-yellow-700' },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700' },
  cancelled:{ label: 'Cancelled',color: 'bg-gray-100 text-gray-500' },
};

function AttendanceTab() {
  const qc = useQueryClient();
  const today = format(new Date(), 'yyyy-MM-dd');
  const [selectedDate, setSelectedDate] = useState(today);

  const { data: users } = useQuery({
    queryKey: ['users-active'],
    queryFn: () => api.get('/users').then(r => r.data),
  });

  const { data: attendance, isLoading } = useQuery({
    queryKey: ['attendance', selectedDate],
    queryFn: () => api.get('/staff/attendance', { params: { date: selectedDate } }).then(r => r.data),
  });

  const saveMutation = useMutation({
    mutationFn: (data: any) => api.post('/staff/attendance/bulk', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['attendance'] }),
  });

  const staffList = users?.users ?? [];
  const attMap = new Map((attendance ?? []).map((a: any) => [a.user_id, a]));

  const [draftMap, setDraftMap] = useState<Record<string, string>>({});

  function setStatus(userId: string, status: string) {
    setDraftMap(m => ({ ...m, [userId]: status }));
  }

  function saveAll() {
    const records = staffList.map((u: any) => ({
      user_id: u.id,
      status: draftMap[u.id] ?? (attMap.get(u.id) as any)?.status ?? 'present',
    }));
    saveMutation.mutate({ attendance_date: selectedDate, records });
    setDraftMap({});
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => {
            const d = new Date(selectedDate); d.setDate(d.getDate() - 1);
            setSelectedDate(format(d, 'yyyy-MM-dd'));
          }} className="p-1.5 rounded-lg hover:bg-gray-100 transition text-gray-500">
            <ChevronLeft size={16} />
          </button>
          <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-orange/30 outline-none" />
          <button onClick={() => {
            const d = new Date(selectedDate); d.setDate(d.getDate() + 1);
            setSelectedDate(format(d, 'yyyy-MM-dd'));
          }} className="p-1.5 rounded-lg hover:bg-gray-100 transition text-gray-500">
            <ChevronRight size={16} />
          </button>
        </div>
        <button
          onClick={saveAll}
          disabled={saveMutation.isPending || Object.keys(draftMap).length === 0}
          className="flex items-center gap-2 bg-orange text-white px-4 py-2 rounded-xl text-sm font-semibold
                     hover:bg-orange/90 disabled:opacity-40 transition">
          {saveMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
          Save Attendance
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-10"><Loader2 size={20} className="animate-spin text-orange" /></div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Staff Member</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Role</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Current Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Mark Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Shift</th>
              </tr>
            </thead>
            <tbody>
              {staffList.map((u: any) => {
                const att = attMap.get(u.id) as any;
                const currentStatus = draftMap[u.id] ?? att?.status ?? 'present';
                const cfg = STATUS_CONFIG[currentStatus as keyof typeof STATUS_CONFIG] ?? STATUS_CONFIG.present;
                return (
                  <tr key={u.id} className="border-b border-gray-50 hover:bg-gray-50/40 transition">
                    <td className="px-4 py-3">
                      <p className="font-medium text-navy">{u.name}</p>
                    </td>
                    <td className="px-4 py-3 text-gray-500 capitalize text-xs">{u.role?.replace(/_/g, ' ')}</td>
                    <td className="px-4 py-3">
                      <span className={clsx('text-xs px-2.5 py-1 rounded-full font-semibold', cfg.color)}>
                        {cfg.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={currentStatus}
                        onChange={e => setStatus(u.id, e.target.value)}
                        className="border border-gray-200 rounded-lg px-2 py-1 text-xs focus:ring-2 focus:ring-orange/30 outline-none">
                        {Object.entries(STATUS_CONFIG).map(([val, cfg]) => (
                          <option key={val} value={val}>{cfg.label} — {val.replace(/_/g, ' ')}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-xs">{att?.shift ?? 'full_day'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function LeavesTab() {
  const qc = useQueryClient();

  const { data: leaves, isLoading } = useQuery({
    queryKey: ['staff-leaves'],
    queryFn: () => api.get('/staff/leaves').then(r => r.data),
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => api.patch(`/staff/leaves/${id}/approve`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['staff-leaves'] }),
  });

  const rejectMutation = useMutation({
    mutationFn: (id: string) => api.patch(`/staff/leaves/${id}/reject`, { reason: 'Rejected' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['staff-leaves'] }),
  });

  const leaveList = leaves ?? [];

  return (
    <div>
      {isLoading ? (
        <div className="flex items-center justify-center py-10"><Loader2 size={20} className="animate-spin text-orange" /></div>
      ) : leaveList.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-card flex flex-col items-center justify-center py-16 text-gray-400">
          <CalendarDays size={36} className="mb-2 opacity-25" />
          <p className="font-medium text-navy">No leave requests</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Staff</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Leave Type</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Period</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Days</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Reason</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody>
              {leaveList.map((l: any) => {
                const statusCfg = LEAVE_STATUS[l.status as keyof typeof LEAVE_STATUS] ?? LEAVE_STATUS.pending;
                return (
                  <tr key={l.id} className="border-b border-gray-50 hover:bg-gray-50/40 transition">
                    <td className="px-4 py-3">
                      <p className="font-medium text-navy">{l.staff_name}</p>
                      <p className="text-xs text-gray-400 capitalize">{l.staff_role?.replace(/_/g, ' ')}</p>
                    </td>
                    <td className="px-4 py-3 capitalize text-gray-600">{l.leave_type?.replace(/_/g, ' ')}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {format(new Date(l.from_date), 'dd MMM')} – {format(new Date(l.to_date), 'dd MMM yyyy')}
                    </td>
                    <td className="px-4 py-3 font-semibold text-navy">{l.total_days}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs max-w-[180px] truncate">{l.reason}</td>
                    <td className="px-4 py-3">
                      <span className={clsx('text-xs px-2.5 py-1 rounded-full font-medium', statusCfg.color)}>
                        {statusCfg.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {l.status === 'pending' && (
                        <div className="flex items-center gap-2">
                          <button onClick={() => approveMutation.mutate(l.id)}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-green-600 hover:bg-green-50 transition">
                            <Check size={14} />
                          </button>
                          <button onClick={() => rejectMutation.mutate(l.id)}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition">
                            <X size={14} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function RosterTab() {
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  const { data: roster, isLoading } = useQuery({
    queryKey: ['roster', selectedDate],
    queryFn: () => api.get('/staff/roster', { params: { date: selectedDate } }).then(r => r.data),
  });

  const shifts = ['morning', 'afternoon', 'evening', 'night'];
  const shiftColors: Record<string, string> = {
    morning:   'bg-yellow-50 border-yellow-200',
    afternoon: 'bg-orange-50 border-orange-200',
    evening:   'bg-purple-50 border-purple-200',
    night:     'bg-blue-50 border-blue-200',
  };

  const byShift = shifts.reduce((acc, s) => {
    acc[s] = (roster ?? []).filter((r: any) => r.shift === s);
    return acc;
  }, {} as Record<string, any[]>);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={() => {
          const d = new Date(selectedDate); d.setDate(d.getDate() - 1);
          setSelectedDate(format(d, 'yyyy-MM-dd'));
        }} className="p-1.5 rounded-lg hover:bg-gray-100 transition text-gray-500">
          <ChevronLeft size={16} />
        </button>
        <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)}
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-orange/30 outline-none" />
        <button onClick={() => {
          const d = new Date(selectedDate); d.setDate(d.getDate() + 1);
          setSelectedDate(format(d, 'yyyy-MM-dd'));
        }} className="p-1.5 rounded-lg hover:bg-gray-100 transition text-gray-500">
          <ChevronRight size={16} />
        </button>
        <span className="text-sm text-gray-500">{format(new Date(selectedDate + 'T00:00:00'), 'EEEE, d MMMM yyyy')}</span>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-10"><Loader2 size={20} className="animate-spin text-orange" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {shifts.map(shift => (
            <div key={shift} className={clsx('rounded-2xl border p-4', shiftColors[shift])}>
              <h3 className="font-semibold text-navy capitalize mb-3 flex items-center gap-2">
                <Clock size={14} /> {shift} Shift
                <span className="ml-auto text-xs font-normal text-gray-400">
                  {byShift[shift].length} staff
                </span>
              </h3>
              {byShift[shift].length === 0 ? (
                <p className="text-xs text-gray-400 italic">No staff assigned</p>
              ) : (
                <div className="space-y-2">
                  {byShift[shift].map((r: any) => (
                    <div key={r.id} className="flex items-center justify-between bg-white/70 rounded-xl px-3 py-2">
                      <div>
                        <p className="text-sm font-medium text-navy">{r.staff_name}</p>
                        <p className="text-xs text-gray-400 capitalize">{r.staff_role?.replace(/_/g, ' ')}</p>
                      </div>
                      <div className="text-xs text-gray-500">{r.start_time} – {r.end_time}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function StaffPage() {
  const [tab, setTab] = useState<Tab>('attendance');

  const { data: stats } = useQuery({
    queryKey: ['staff-stats'],
    queryFn: () => api.get('/staff/stats').then(r => r.data),
    staleTime: 60_000,
  });

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-navy flex items-center gap-2">
            <CalendarDays size={22} className="text-orange" /> Staff Management
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Attendance, duty roster and leave management</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Staff',     value: stats?.total_staff,       color: 'bg-navy',        icon: Users },
          { label: 'Present Today',   value: stats?.present_today,     color: 'bg-green-500',   icon: CheckCircle2 },
          { label: 'Absent Today',    value: stats?.absent_today,      color: 'bg-red-400',     icon: XCircle },
          { label: 'Pending Leaves',  value: stats?.pending_leave_requests, color: 'bg-yellow-500', icon: AlertCircle },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-100 shadow-card p-4 flex items-center gap-3">
            <div className={clsx('w-10 h-10 rounded-xl flex items-center justify-center', s.color)}>
              <s.icon size={18} className="text-white" />
            </div>
            <div>
              <div className="text-2xl font-bold text-navy">{s.value ?? 0}</div>
              <div className="text-xs text-gray-500">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-5 w-fit">
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={clsx('px-4 py-1.5 rounded-lg text-sm font-medium capitalize transition-all',
              tab === t ? 'bg-white text-navy shadow-sm' : 'text-gray-500 hover:text-gray-700')}>
            {t.replace('_', ' ')}
          </button>
        ))}
      </div>

      {tab === 'attendance' && <AttendanceTab />}
      {tab === 'roster'     && <RosterTab />}
      {tab === 'leaves'     && <LeavesTab />}
    </div>
  );
}
